import { ButtonSize, ButtonType, IconButton } from '@ardoq/button';
import { space } from '@ardoq/design-tokens';
import {
  ComponentProps,
  ComponentWithDropdown,
  DropdownItem,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { DropdownButtonOptionType } from '@ardoq/dropdown-menu-ui';
import { IconName } from '@ardoq/icons';
import { NodeViewContent, NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

export type TableNodeViewLabels = {
  alignLeft: string;
  alignCenter: string;
  alignRight: string;
  addColumnBefore: string;
  addColumnAfter: string;
  deleteColumn: string;
  addRow: string;
  deleteTable: string;
  addRowBefore: string;
  addRowAfter: string;
  deleteRow: string;
};

export const defaultTableNodeViewLabels: TableNodeViewLabels = {
  alignLeft: 'Align left',
  alignCenter: 'Align center',
  alignRight: 'Align right',
  addColumnBefore: 'Add column before',
  addColumnAfter: 'Add column after',
  deleteColumn: 'Delete column',
  addRow: 'Add row',
  deleteTable: 'Delete table',
  addRowBefore: 'Add row before',
  addRowAfter: 'Add row after',
  deleteRow: 'Delete row',
};

const StyledMenuButton = styled(IconButton)<{ $align: string }>`
  cursor: pointer;
  padding: 0;
  position: absolute;
  margin: 0;
  min-height: 24px;
  line-height: 24px !important;
  ${({ $align }) =>
    $align === 'right'
      ? css`
          top: ${space.xxsmall};
          left: ${space.xxsmall};
        `
      : css`
          top: ${space.xxsmall};
          right: ${space.xxsmall};
        `};
`;

type MenuProps = {
  align: string;
} & ComponentProps;

const TableNodeView = ({
  node,
  menuOptions,
  menuButtonLabel,
}: NodeViewProps & {
  menuOptions: Partial<DropdownItem>[];
  menuButtonLabel: string;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [hasFocus, setHasFocus] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    /**
     * I could not find a better way to detect if the parent element has focus.
     * For some reason, the select prop is always false. By using `MutationObserver`,
     * we can listen for changes in the parent element and check if it has the class
     * `has-focus`. This class is added by the Focus extension when the parent element
     * gains focus.
     */
    const observer = new MutationObserver(() => {
      if (wrapperRef.current?.parentElement?.classList.contains('has-focus')) {
        setHasFocus(true);
      } else {
        setHasFocus(false);
      }
    });

    if (wrapperRef.current && wrapperRef.current.parentElement) {
      observer.observe(wrapperRef.current.parentElement, {
        attributes: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <NodeViewWrapper ref={wrapperRef}>
      {(hasFocus || isOpen) && (
        <ComponentWithDropdown
          alignMenuTo={node.attrs.align === 'right' ? 'left' : 'right'}
          options={menuOptions}
          onMenuOpen={() => {
            setIsOpen(true);
          }}
          onMenuClose={() => {
            setIsOpen(false);
          }}
          Component={({ onMenuOpen, align, ref }: MenuProps) => (
            <StyledMenuButton
              type="button"
              $align={align}
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              label={menuButtonLabel}
              ref={ref}
              onClick={onMenuOpen}
              buttonType={ButtonType.TERTIARY}
              buttonSize={ButtonSize.SMALL}
              iconName={IconName.CHEVRON_DOWN}
            />
          )}
          componentProps={{ align: node.attrs.align }}
        />
      )}
      <NodeViewContent />
    </NodeViewWrapper>
  );
};

const resolveLabels = (
  editor: NodeViewProps['editor']
): { labels: TableNodeViewLabels; menuButtonLabel: string } => {
  const tableStorage = editor.extensionStorage.table;
  return {
    labels: tableStorage?.labels ?? defaultTableNodeViewLabels,
    menuButtonLabel: tableStorage?.menuButtonLabel ?? 'Menu',
  };
};

export const TableHeaderNodeView = (nodeViewProps: NodeViewProps) => {
  const { editor } = nodeViewProps;
  const { labels, menuButtonLabel } = resolveLabels(editor);
  return (
    <TableNodeView
      {...nodeViewProps}
      menuButtonLabel={menuButtonLabel}
      menuOptions={[
        {
          label: labels.alignLeft,
          onClick: () => editor.chain().setColumnAlignment('left').run(),
        },
        {
          label: labels.alignCenter,
          onClick: () => editor.chain().setColumnAlignment('center').run(),
        },
        {
          label: labels.alignRight,
          onClick: () => editor.chain().setColumnAlignment('right').run(),
        },
        {
          type: DropdownOptionType.DIVIDER,
        },
        {
          label: labels.addColumnBefore,
          onClick: () => editor.chain().focus().addColumnBefore().run(),
        },
        {
          label: labels.addColumnAfter,
          onClick: () => editor.chain().focus().addColumnAfter().run(),
        },
        {
          label: labels.deleteColumn,
          buttonType: DropdownButtonOptionType.DANGER,
          onClick: () => editor.chain().focus().deleteColumn().run(),
        },
        {
          type: DropdownOptionType.DIVIDER,
        },
        {
          label: labels.addRow,
          onClick: () =>
            editor
              .chain()
              .focus()
              .addRowAfter()
              .updateAlignmentInRowAfter() // Ensure new row has the same alignment as the row before
              .run(),
        },
        {
          type: DropdownOptionType.DIVIDER,
        },
        {
          label: labels.deleteTable,
          buttonType: DropdownButtonOptionType.DANGER,
          onClick: () => editor.chain().focus().deleteTable().run(),
        },
      ]}
    />
  );
};

export const TableCellNodeView = (nodeViewProps: NodeViewProps) => {
  const { editor } = nodeViewProps;
  const { labels, menuButtonLabel } = resolveLabels(editor);
  return (
    <TableNodeView
      {...nodeViewProps}
      menuButtonLabel={menuButtonLabel}
      menuOptions={[
        {
          label: labels.addRowBefore,
          onClick: () =>
            editor
              .chain()
              .focus()
              .addRowBefore()
              .updateAlignmentInRowBefore() // Ensure new row has the same alignment as the row before
              .run(),
        },
        {
          label: labels.addRowAfter,
          onClick: () =>
            editor
              .chain()
              .focus()
              .addRowAfter()
              .updateAlignmentInRowAfter() // Ensure new row has the same alignment as the row before
              .run(),
        },
        {
          label: labels.deleteRow,
          buttonType: DropdownButtonOptionType.DANGER,
          onClick: () => editor.chain().focus().deleteRow().run(),
        },
        {
          type: DropdownOptionType.DIVIDER,
        },
        {
          label: labels.addColumnBefore,
          onClick: () => editor.chain().focus().addColumnBefore().run(),
        },
        {
          label: labels.addColumnAfter,
          onClick: () => editor.chain().focus().addColumnAfter().run(),
        },
        {
          label: labels.deleteColumn,
          buttonType: DropdownButtonOptionType.DANGER,
          onClick: () => editor.chain().focus().deleteColumn().run(),
        },
      ]}
    />
  );
};
